import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import { useQueryGetMindHealthReport } from '@/api/mentalHealth/mindHealthReport';
import useQueryGetUserDetail from '@/api/auth/getUser';
import VitalSignsChart from '@/component/mentalHealth/VitalSignsChart';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import BorderButton from '@/component/button/BorderButton';
import { toast } from 'react-toastify';
import { exportElementToPdf } from '@/util/pdfExport.ts';
import { createRoot } from 'react-dom/client';
import MentalHealthReportPDFTemplate from './MentalHealthReportPDFTemplate';
import MentalHealthReportDetailedPDFTemplate from './MentalHealthReportDetailedPDFTemplate';
import { numberOr } from '@/util/mentalHealthFormat';
import { useAppLocale } from '@/locales/locale';

const MentalHealthReportDetail: React.FC = () => {
  const { t } = useTranslation();
  const { intlLocale } = useAppLocale();
  const { id, protecteeId: protecteeIdParam, sessionId: sessionIdParam } =
    useParams<{ id?: string; protecteeId?: string; sessionId?: string }>();
  const [legacySearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'symptoms' | 'charts' | 'raw'>('overview');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const protecteeId = Number(protecteeIdParam) || 0;
  const sessionId = sessionIdParam || '';

  // 계산이 아직 안 끝났으면 서버가 404 가 아니라 PENDING 을 주면서 큐에 넣는다.
  const {
    data: serverReport,
    isLoading,
    error,
  } = useQueryGetMindHealthReport(protecteeId, sessionId, protecteeId > 0, true);
  const { data: user } = useQueryGetUserDetail(protecteeId > 0 ? protecteeId : undefined);

  const report = serverReport?.report;
  const reportData = useMemo(
    () =>
      serverReport
        ? {
            userId: protecteeId,
            userName: user?.name,
            analysisDate: serverReport.computedAt,
          }
        : undefined,
    [serverReport, protecteeId, user?.name]
  );

  const translateValue = (value: string | undefined, prefix?: string): string => {
    if (!value) return '';
    let translationKey: string;
    if (value.startsWith('mentalHealth.')) {
      translationKey = value;
    } else if (prefix) {
      translationKey = `${prefix}.${value}`;
    } else {
      return value;
    }
    const translated = t(translationKey);
    if (translated === translationKey) {
      return value;
    }
    return translated;
  };

  const renderAndDownloadPdf = async (
    template: React.ReactElement,
    templateId: string,
    fileName: string,
    renderDelayMs: number
  ) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-10000px';
    container.style.left = '-10000px';
    document.body.appendChild(container);
    const root = createRoot(container);

    try {
      root.render(template);
      await new Promise(resolve => setTimeout(resolve, renderDelayMs));
      const element = container.querySelector(templateId) as HTMLElement;
      if (!element) throw new Error('PDF template not found');
      await exportElementToPdf(element, fileName);
    } finally {
      root.unmount();
      container.remove();
    }
  };

  // 구 경로(/mental-health/reports/:id)로 들어온 링크.
  // 예전에는 리포트가 브라우저 localStorage 에만 있어서 대상자를 몰라도 열렸다.
  // 서버 조회는 (protecteeId, sessionId) 가 있어야 하므로 ?userId= 가 있으면 새 경로로 넘기고,
  // 없으면 서버에서 찾을 방법이 없다.
  if (!protecteeIdParam) {
    const legacyUserId = Number(legacySearchParams.get('userId')) || 0;
    if (legacyUserId > 0 && id) {
      return (
        <Navigate
          replace
          to={`/mental-health/protectee/${legacyUserId}/reports/${id}`}
        />
      );
    }

    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="mb-2 font-semibold text-gray-900">
              {t('mentalHealth.reportDetail.linkExpired')}
            </p>
            <p className="mb-4 text-sm text-gray-600">
              {t('mentalHealth.reportDetail.linkExpired.desc')}
            </p>
            <BorderButton
              text={t('mentalHealth.reports.button.dashboard')}
              onClick={() => navigate('/mental-health')}
            />
          </div>
        </div>
      </PageConfig>
    );
  }

  if (isLoading) {
    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}...</p>
          </div>
        </div>
      </PageConfig>
    );
  }

  // 계산 전이면 404 가 아니라 PENDING 이 온다. 조회 자체가 큐를 깨우므로 폴링만 하면 된다.
  if (serverReport?.status === 'PENDING') {
    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 font-semibold text-gray-900">
              {t('mentalHealth.reportDetail.analyzing')}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {t('mentalHealth.reportDetail.analyzing.desc')}
            </p>
          </div>
        </div>
      </PageConfig>
    );
  }

  if (error || !report) {
    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">{t('common.error')}</p>
            <BorderButton text={t('mentalHealth.reports.button.dashboard')} onClick={() => navigate('/mental-health')} />
          </div>
        </div>
      </PageConfig>
    );
  }

  // 요약 다운로드 (기존 형식)
  const handleDownloadSummary = async () => {
    setShowDownloadMenu(false);
    try {
      toast.info(t('mentalHealth.reportDetail.generatingPDF'), { autoClose: false, toastId: 'pdf-gen' });

      const date = reportData?.analysisDate ? new Date(reportData.analysisDate) : new Date();
      const dateStr = date.toISOString().split('T')[0];
      const userName = reportData?.userName || 'user';
      const fileName = `health-report-summary-${userName}-${dateStr}.pdf`;
      await renderAndDownloadPdf(
        <MentalHealthReportPDFTemplate report={report} user={user} />,
        '#pdf-template',
        fileName,
        1500
      );
      toast.dismiss('pdf-gen');
      toast.success(t('mentalHealth.reportDetail.downloadSuccess'));

    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.dismiss('pdf-gen');
      toast.error(t('common.error'));
    }
  };

  // 상세 다운로드 (모든 내용 포함)
  const handleDownloadDetailed = async () => {
    setShowDownloadMenu(false);
    try {
      toast.info(t('mentalHealth.reportDetail.generatingPDF'), { autoClose: false, toastId: 'pdf-gen' });

      const date = reportData?.analysisDate ? new Date(reportData.analysisDate) : new Date();
      const dateStr = date.toISOString().split('T')[0];
      const userName = reportData?.userName || 'user';
      const fileName = `health-report-detailed-${userName}-${dateStr}.pdf`;
      await renderAndDownloadPdf(
        <MentalHealthReportDetailedPDFTemplate
          report={report}
          user={user}
          analysisDate={reportData?.analysisDate ?? undefined}
        />,
        '#pdf-template-detailed',
        fileName,
        2000
      );
      toast.dismiss('pdf-gen');
      toast.success(t('mentalHealth.reportDetail.downloadSuccess'));

    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.dismiss('pdf-gen');
      toast.error(t('common.error'));
    }
  };

  const tabs = [
    { id: 'overview', label: t('mentalHealth.detail.overview'), icon: 'OV' },
    { id: 'symptoms', label: t('mentalHealth.detail.symptoms'), icon: 'RS' },
    { id: 'charts', label: t('mentalHealth.detail.charts'), icon: 'CH' },
    { id: 'raw', label: t('mentalHealth.detail.rawData'), icon: 'DT' },
  ];

  const healthScore = Math.round(report.mindScore?.score || 0);
  const dataQuality = Math.round((report.qualityMetrics?.analyzability || 0) * 100);
  const analysisDate = reportData?.analysisDate
    ? new Date(reportData.analysisDate).toLocaleDateString(intlLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    : '-';
  const scoreLevel = healthScore >= 80 ? 'healthy' : healthScore >= 70 ? 'normal' : healthScore >= 55 ? 'caution' : 'risk';
  const recommendationTitle = healthScore >= 80
    ? t('mentalHealth.detail.excellentHealth')
    : healthScore >= 70
      ? t('mentalHealth.detail.goodState')
      : healthScore >= 55
        ? t('mentalHealth.detail.cautionNeeded')
        : t('mentalHealth.detail.consultationRecommended');
  const recommendationItems = healthScore >= 80
    ? [
      t('mentalHealth.detail.excellentHealthDesc1'),
      t('mentalHealth.detail.excellentHealthDesc2'),
      t('mentalHealth.detail.excellentHealthDesc3')
    ]
    : healthScore >= 70
      ? [
        t('mentalHealth.detail.goodStateDesc1'),
        t('mentalHealth.detail.goodStateDesc2'),
        t('mentalHealth.detail.goodStateDesc3')
      ]
      : healthScore >= 55
        ? [
          t('mentalHealth.detail.cautionDesc1'),
          t('mentalHealth.detail.cautionDesc2'),
          t('mentalHealth.detail.cautionDesc3')
        ]
        : [
          t('mentalHealth.detail.consultDesc1'),
          t('mentalHealth.detail.consultDesc2'),
          t('mentalHealth.detail.consultDesc3')
        ];
  const symptomPredictions = report.symptomPredictions
    ? Object.values(report.symptomPredictions).sort((a, b) => {
      const priority: Record<string, number> = { very_high: 0, high: 1, moderate: 2, low: 3 };
      return (priority[a.riskLevel] ?? 4) - (priority[b.riskLevel] ?? 4);
    })
    : [];
  const attentionPredictionCount = symptomPredictions.filter(
    prediction => prediction.riskLevel === 'high' || prediction.riskLevel === 'very_high'
  ).length;
  const selectedWindowsCount = report.mindScore.selectedWindows?.length || 0;

  const handleCopyRawData = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      toast.success(t('mentalHealth.reportDetail.raw.copySuccess'));
    } catch {
      toast.error(t('mentalHealth.reportDetail.raw.copyFailed'));
    }
  };

  return (
    <PageConfig showTopNav={true} showSideNav={true}>
      <div className="health-report-detail p-6 sm:p-4">
        <div className="mx-auto w-full max-w-[1440px] space-y-6">
        {/* 헤더 */}
        <div className="health-report-detail__heading flex justify-between items-center gap-4 md:flex-col md:items-stretch">
          <div>
            <span>AI BIOMETRIC HEALTH ANALYSIS</span>
            <h1 className="text-2xl font-bold text-gray-900">{t('mentalHealth.reportDetail.title')}</h1>
            <p className="text-gray-600 mt-1">{t('mentalHealth.reportDetail.subtitle')}</p>
          </div>
          <div className="flex gap-3 sm:flex-col">
            <BorderButton text={t('mentalHealth.reportDetail.backToList')} onClick={() => navigate(-1)} />
            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('mentalHealth.reportDetail.download')}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={handleDownloadSummary}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center"
                  >
                    <span className="mr-2">📄</span>
                    {t('mentalHealth.reportDetail.downloadSummary')}
                  </button>
                  <button
                    onClick={handleDownloadDetailed}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center border-t border-gray-100"
                  >
                    <span className="mr-2">📋</span>
                    {t('mentalHealth.reportDetail.downloadDetailed')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="health-report-detail__tabs overflow-x-auto overflow-y-hidden">
          <nav className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 탭 컨텐츠 */}
        {activeTab === 'overview' && (
          <div className="health-report-summary">
            <section className={`health-report-summary__hero health-report-summary__hero--${scoreLevel}`}>
              <div className="health-report-summary__identity">
                <span>RESULT SUMMARY</span>
                <h2>{reportData?.userName || user?.name || t('mentalHealth.reportDetail.comprehensive')}</h2>
                <p>{analysisDate}</p>
              </div>
              <div className="health-report-summary__score">
                <small>{t('mentalHealth.detail.mindHealthScore')}</small>
                <strong>{healthScore}</strong>
                <span>/ 100</span>
              </div>
              <div className="health-report-summary__conclusion">
                <span>{t(`mentalHealth.status.${report.decisionLabel}`)}</span>
                <h3>{recommendationTitle}</h3>
                <p>{translateValue(report.stressAnalysis.description)}</p>
              </div>
            </section>

            <section className="health-report-section">
              <div className="health-report-section__heading">
                <div>
                  <span>KEY RESULTS</span>
                  <h3>{t('mentalHealth.reportDetail.overview.keyResults.title')}</h3>
                  <p>{t('mentalHealth.reportDetail.overview.keyResults.desc')}</p>
                </div>
                <div className="health-report-section__quality">
                  <span>{t('mentalHealth.reportDetail.dataQuality')}</span>
                  <strong>{dataQuality}%</strong>
                </div>
              </div>

              <div className="health-report-result-grid">
                <article className="health-report-result">
                  <span className="health-report-result__code">HR</span>
                  <div>
                    <small>{t('mentalHealth.reportDetail.heartRate')}</small>
                    <strong>{report.mindScore.components?.HR_med?.toFixed(0) || '-'} <em>bpm</em></strong>
                    <p>{t('mentalHealth.reportDetail.overview.heartRate.desc')}</p>
                  </div>
                </article>
                <article className="health-report-result">
                  <span className="health-report-result__code">BR</span>
                  <div>
                    <small>{t('mentalHealth.reportDetail.respiration')}</small>
                    <strong>{report.mindScore.components?.Resp_med?.toFixed(1) || '-'} <em>{t('mentalHealth.reportDetail.rpm')}</em></strong>
                    <p>{translateValue(report.respirationState, 'mentalHealth.respiration')}</p>
                  </div>
                </article>
                <article className={`health-report-result health-report-result--${report.stressAnalysis.category}`}>
                  <span className="health-report-result__code">ST</span>
                  <div>
                    <small>{t('mentalHealth.reportDetail.stressLevel')}</small>
                    <strong>{t(`mentalHealth.stress.${report.stressAnalysis.category}`)}</strong>
                    <p>{t('mentalHealth.reportDetail.overview.stress.desc')}</p>
                  </div>
                </article>
                <article className={`health-report-result health-report-result--${report.balanceAnalysis.flag}`}>
                  <span className="health-report-result__code">AN</span>
                  <div>
                    <small>{t('mentalHealth.detail.autonomicBalance')}</small>
                    <strong>{translateValue(report.balanceAnalysis.flag, 'mentalHealth.balance')}</strong>
                    <p>{t('mentalHealth.reportDetail.overview.balance.desc')}</p>
                  </div>
                </article>
              </div>
            </section>

            <div className="health-report-summary__decision-grid">
              <section className={`health-report-section health-report-action health-report-action--${scoreLevel}`}>
                <div className="health-report-section__heading">
                  <div>
                    <span>NEXT ACTION</span>
                    <h3>{t('mentalHealth.reportDetail.overview.action.title')}</h3>
                    <p>{recommendationTitle}</p>
                  </div>
                </div>
                <ol>
                  {recommendationItems.map((item, index) => (
                    <li key={item}>
                      <span>{index + 1}</span>
                      <p>{item}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="health-report-section health-report-meaning">
                <div className="health-report-section__heading">
                  <div>
                    <span>WHAT IT MEANS</span>
                    <h3>{t('mentalHealth.reportDetail.overview.meaning.title')}</h3>
                  </div>
                </div>
                <div>
                  <article>
                    <span>01</span>
                    <div>
                      <strong>{t('mentalHealth.detail.stressState')}</strong>
                      <p>{translateValue(report.stressAnalysis.description)}</p>
                    </div>
                  </article>
                  <article>
                    <span>02</span>
                    <div>
                      <strong>{t('mentalHealth.detail.autonomicBalanceShort')}</strong>
                      <p>{translateValue(report.balanceAnalysis.description)}</p>
                    </div>
                  </article>
                </div>
              </section>
            </div>

            <section className="health-report-section health-report-habits">
              <div className="health-report-section__heading">
                <div>
                  <span>DAILY CARE</span>
                  <h3>{t('mentalHealth.detail.lifestyleGuide')}</h3>
                  <p>{t('mentalHealth.reportDetail.overview.habits.desc')}</p>
                </div>
              </div>
              <div className="health-report-habits__grid">
                <div>
                  <strong>{t('mentalHealth.detail.recommendedActions')}</strong>
                  <ul>
                    <li>{t('mentalHealth.detail.regularSleep')}</li>
                    <li>{t('mentalHealth.detail.moderateExercise')}</li>
                    <li>{t('mentalHealth.detail.balancedDiet')}</li>
                  </ul>
                </div>
                <div>
                  <strong>{t('mentalHealth.detail.precautions')}</strong>
                  <ul>
                    <li>{t('mentalHealth.detail.excessiveCaffeine')}</li>
                    <li>{t('mentalHealth.detail.irregularLifestyle')}</li>
                    <li>{t('mentalHealth.detail.chronicStress')}</li>
                  </ul>
                </div>
              </div>
            </section>

            <details className="health-report-technical">
              <summary>
                <span>
                  <small>FOR PROFESSIONALS</small>
                  <strong>{t('mentalHealth.detail.hrvDetailedAnalysis')}</strong>
                  <p>{t('mentalHealth.reportDetail.overview.technical.desc')}</p>
                </span>
                <i>+</i>
              </summary>
              <div className="health-report-technical__content">
                <div className="health-report-technical__metrics">
                  <article>
                    <span>RMSSD</span>
                    <strong>{report.mindScore.components?.RMSSD_med?.toFixed(1) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.overview.technical.rmssd')}</small>
                  </article>
                  <article>
                    <span>LF / HF</span>
                    <strong>{report.psdData?.LF_HF?.toFixed(2) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.overview.technical.lfhf')}</small>
                  </article>
                  <article>
                    <span>LF</span>
                    <strong>{report.psdData?.LFnu?.toFixed(1) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.overview.technical.lf')}</small>
                  </article>
                  <article>
                    <span>HF</span>
                    <strong>{report.psdData?.HFnu?.toFixed(1) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.overview.technical.hf')}</small>
                  </article>
                  <article>
                    <span>TP</span>
                    <strong>{report.psdData?.TP?.toFixed(0) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.overview.technical.tp')}</small>
                  </article>
                  <article>
                    <span>{t('mentalHealth.reportDetail.duration')}</span>
                    <strong>{report.qualityMetrics?.duration_min?.toFixed(1) || 'N/A'}</strong>
                    <small>{t('mentalHealth.reportDetail.minutes')}</small>
                  </article>
                </div>
                <div className="health-report-technical__note">
                  <strong>{t('mentalHealth.detail.hrvInterpretation')}</strong>
                  <p>
                    {numberOr(report.psdData.LF_HF) > 2.5
                      ? t('mentalHealth.detail.sympatheticDominantStress')
                      : numberOr(report.psdData.LF_HF) < 0.5
                        ? t('mentalHealth.detail.parasympatheticDominantExcessiveRelaxation')
                        : t('mentalHealth.detail.autonomicBalanceState')}
                  </p>
                </div>
              </div>
            </details>
          </div>
        )}

        {activeTab === 'symptoms' && (
          <div className="health-report-tab">
            <section className="health-report-tab__intro">
              <div>
                <span>RISK SCREENING</span>
                <h2>{t('mentalHealth.detail.symptomPredictionTitle')}</h2>
                <p>{t('mentalHealth.reportDetail.symptoms.intro')}</p>
              </div>
              <div className="health-report-tab__summary">
                <div>
                  <span>{t('mentalHealth.reportDetail.label.analysisItems')}</span>
                  <strong>{symptomPredictions.length}</strong>
                  <small>{t('mentalHealth.reportDetail.unit.items')}</small>
                </div>
                <div className={attentionPredictionCount > 0 ? 'is-attention' : ''}>
                  <span>{t('mentalHealth.reportDetail.label.attentionCheck')}</span>
                  <strong>{attentionPredictionCount}</strong>
                  <small>{t('mentalHealth.reportDetail.unit.items')}</small>
                </div>
              </div>
            </section>

            {symptomPredictions.length > 0 ? (
              <div className="health-symptom-screening">
                {symptomPredictions.map(prediction => (
                  <SymptomPredictionCard key={prediction.symptomName} prediction={prediction} />
                ))}
              </div>
            ) : (
              <div className="health-report-tab__empty">
                <span>0</span>
                <h3>{t('mentalHealth.detail.symptomPredictionNoData')}</h3>
                <p>{t('mentalHealth.detail.symptomPredictionOldReport')}</p>
              </div>
            )}

            <section className="health-report-explainer">
              <div>
                <span>HOW TO READ</span>
                <h3>{t('mentalHealth.detail.symptomIndicatorGuide')}</h3>
              </div>
              <ol>
                <li><span>1</span><p><Trans i18nKey="mentalHealth.reportDetail.symptoms.guide.item1" components={{ strong: <strong /> }} /></p></li>
                <li><span>2</span><p><Trans i18nKey="mentalHealth.reportDetail.symptoms.guide.item2" components={{ strong: <strong /> }} /></p></li>
                <li><span>3</span><p><Trans i18nKey="mentalHealth.reportDetail.symptoms.guide.item3" components={{ strong: <strong /> }} /></p></li>
              </ol>
            </section>
            <div className="health-report-tab__notice" dangerouslySetInnerHTML={{ __html: t('mentalHealth.detail.symptomNote') }} />
          </div>
        )}

        {activeTab === 'charts' && (
          <div className="health-report-tab">
            <section className="health-report-tab__intro">
              <div>
                <span>TIME SERIES</span>
                <h2>{t('mentalHealth.detail.charts')}</h2>
                <p>{t('mentalHealth.reportDetail.charts.intro')}</p>
              </div>
              <div className="health-report-tab__summary">
                <div>
                  <span>{t('mentalHealth.reportDetail.label.analysisWindows')}</span>
                  <strong>{selectedWindowsCount}</strong>
                  <small>{t('mentalHealth.reportDetail.unit.windows')}</small>
                </div>
                <div>
                  <span>{t('mentalHealth.reportDetail.label.measurementQuality')}</span>
                  <strong>{dataQuality}</strong>
                  <small>%</small>
                </div>
              </div>
            </section>

            {report.mindScore.selectedWindows && selectedWindowsCount > 0 ? (
              <>
                <div className="health-vital-charts">
                  <VitalSignsChart windows={report.mindScore.selectedWindows} type="hr" />
                  <VitalSignsChart windows={report.mindScore.selectedWindows} type="resp" />
                  <VitalSignsChart windows={report.mindScore.selectedWindows} type="rmssd" />
                  <VitalSignsChart windows={report.mindScore.selectedWindows} type="dqi" />
                </div>
                <section className="health-report-explainer">
                  <div>
                    <span>CHART GUIDE</span>
                    <h3>{t('mentalHealth.reportDetail.charts.guide.title')}</h3>
                  </div>
                  <ol>
                    <li><span>1</span><p><Trans i18nKey="mentalHealth.reportDetail.charts.guide.item1" components={{ strong: <strong /> }} /></p></li>
                    <li><span>2</span><p>{t('mentalHealth.reportDetail.charts.guide.item2')}</p></li>
                    <li><span>3</span><p>{t('mentalHealth.reportDetail.charts.guide.item3')}</p></li>
                  </ol>
                </section>
              </>
            ) : (
              <div className="health-report-tab__empty">
                <span>CH</span>
                <h3>{t('mentalHealth.reportDetail.charts.empty.title')}</h3>
                <p>{t('mentalHealth.reportDetail.charts.empty.desc')}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'raw' && (
          <div className="health-report-tab">
            <section className="health-report-tab__intro">
              <div>
                <span>ANALYSIS DATA</span>
                <h2>{t('mentalHealth.detail.rawData')}</h2>
                <p>{t('mentalHealth.reportDetail.raw.intro')}</p>
              </div>
              <button type="button" className="health-raw-data__copy" onClick={handleCopyRawData}>
                {t('mentalHealth.reportDetail.raw.copyJson')}
              </button>
            </section>

            <section className="health-raw-data__overview">
              <article><span>{t('mentalHealth.reportDetail.label.reportTarget')}</span><strong>{reportData?.userName || user?.name || '-'}</strong></article>
              <article><span>{t('mentalHealth.score.health')}</span><strong>{healthScore} / 100</strong></article>
              <article><span>{t('mentalHealth.reportDetail.label.measurementQuality')}</span><strong>{dataQuality}%</strong></article>
              <article><span>{t('mentalHealth.reportDetail.label.analysisWindows')}</span><strong>{t('mentalHealth.reportDetail.raw.windowsValue', { count: selectedWindowsCount })}</strong></article>
              <article><span>{t('mentalHealth.reportDetail.label.measurementTime')}</span><strong>{t('mentalHealth.reportDetail.raw.minutesValue', { value: report.qualityMetrics?.duration_min?.toFixed(1) || '-' })}</strong></article>
              <article><span>{t('mentalHealth.reportDetail.label.decisionStatus')}</span><strong>{t(`mentalHealth.status.${report.decisionLabel}`)}</strong></article>
            </section>

            <section className="health-raw-data__dictionary">
              <div>
                <span>FIELD GUIDE</span>
                <h3>{t('mentalHealth.reportDetail.raw.fieldGuide.title')}</h3>
              </div>
              <dl>
                <div><dt>mindScore</dt><dd>{t('mentalHealth.reportDetail.raw.field.mindScore')}</dd></div>
                <div><dt>stressAnalysis</dt><dd>{t('mentalHealth.reportDetail.raw.field.stressAnalysis')}</dd></div>
                <div><dt>balanceAnalysis</dt><dd>{t('mentalHealth.reportDetail.raw.field.balanceAnalysis')}</dd></div>
                <div><dt>psdData</dt><dd>{t('mentalHealth.reportDetail.raw.field.psdData')}</dd></div>
                <div><dt>qualityMetrics</dt><dd>{t('mentalHealth.reportDetail.raw.field.qualityMetrics')}</dd></div>
                <div><dt>selectedWindows</dt><dd>{t('mentalHealth.reportDetail.raw.field.selectedWindows')}</dd></div>
              </dl>
            </section>

            <details className="health-raw-data__json">
              <summary>
                <span><small>FULL JSON</small><strong>{t('mentalHealth.reportDetail.raw.fullJson.title')}</strong><p>{t('mentalHealth.reportDetail.raw.fullJson.desc')}</p></span>
                <i>+</i>
              </summary>
              <pre>{JSON.stringify(report, null, 2)}</pre>
            </details>
          </div>
        )}

        {/* 주의사항 */}
        <div className="health-report-disclaimer" dangerouslySetInnerHTML={{ __html: t('mentalHealth.detail.rawDataNote') }} />
        </div>
      </div>
    </PageConfig>
  );
};

export default MentalHealthReportDetail;
